import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Zap, Plus, TrendingUp, User, LogOut, Sparkles, Video, Calendar, Rocket, Check, Star, ShoppingCart, Settings, Clock, XCircle, CreditCard, Gift } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Détection automatique du domaine pour la production
const getApiUrl = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname === 'boost-strategia.com' || window.location.hostname === 'www.boost-strategia.com') {
      return 'https://boost-strategia.com';
    }
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
const API = `${getApiUrl()}/api`;

const TOTAL_STEPS = 50; // 4 parties principales (50 étapes au total)
const FREE_STEPS_LIMIT = 3; // Limite pour les utilisateurs gratuits

const Dashboard = () => {
  const [studies, setStudies] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [studiesRes, subRes, userRes] = await Promise.all([
        axios.get(`${API}/studies/list`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/subscriptions/current`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setStudies(studiesRes.data);
      setSubscription(subRes.data);
      setUser(userRes.data);
      console.log('User loaded:', userRes.data);  // Debug log
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Une erreur est survenue lors du chargement');
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        navigate('/auth');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleCancelTrial = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler votre essai gratuit ? Vous ne serez pas débité.')) {
      return;
    }
    
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/subscription/cancel-trial`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Votre essai gratuit a été annulé. Vous ne serez pas débité.');
      fetchData(); // Recharger les données
    } catch (error) {
      console.error('Cancel trial error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  const handleNewStudy = () => {
    // Vérifier si le trial est expiré
    if (subscription?.status === 'trial_expired') {
      toast.error('Votre essai gratuit est terminé. Passez à un abonnement pour continuer.');
      navigate('/pricing');
      return;
    }

    // Les utilisateurs gratuits peuvent créer UNE étude avec 3 étapes gratuites
    // Vérifier s'ils ont déjà une étude en cours
    if (subscription?.plan === 'free' && studies.length > 0) {
      toast.info('Avec le plan gratuit, tu peux avoir une seule étude. Passe à un plan payant pour en créer d\'autres !');
      navigate('/pricing');
      return;
    }

    if (subscription?.plan !== 'free' && subscription?.studies_remaining <= 0) {
      toast.error('Tu n\'as plus d\'études disponibles. Achète des crédits supplémentaires.');
      navigate('/profile');
      return;
    }

    navigate('/onboarding');
  };

  const handleBuyExtraStudy = async () => {
    const token = localStorage.getItem('token');
    try {
      toast.loading('Redirection vers le paiement...');
      const response = await axios.post(
        `${API}/payments/checkout/session`,
        {
          extra_studies: 1,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const checkoutUrl = response.data.checkout_url || response.data.url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        toast.dismiss();
        toast.error('Erreur: URL de paiement non reçue');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.dismiss();
      toast.error('Erreur lors de la création du paiement');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
              <Zap className="w-6 h-6 text-black" />
            </div>
            <span className="text-2xl font-heading font-bold">Boost-StrategIA</span>
          </Link>

          <nav className="flex items-center gap-4">
            {/* Admin link - only for admin emails */}
            {user?.email && ['admin@boost-strategia.com', 'valerie.madej@gmail.com'].includes(user.email.toLowerCase()) && (
              <Link to="/admin" data-testid="admin-link">
                <Button variant="ghost" size="sm" className="text-highlight">
                  <Settings className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              </Link>
            )}
            <Link to="/profile" data-testid="profile-link">
              <Button variant="ghost" size="sm">
                <User className="w-4 h-4 mr-2" />
                Profil
              </Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Bannière Trial Expiré - Page de suspension */}
        {subscription?.status === 'trial_expired' && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="p-8 border-2 border-red-500/50 mb-8 bg-gradient-to-r from-red-500/10 to-orange-500/10" data-testid="trial-expired-banner">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-red-400" />
                </div>
                <h2 className="font-heading font-bold text-2xl text-red-400 mb-2">
                  Votre essai gratuit est terminé
                </h2>
                <p className="text-muted mb-6 max-w-md mx-auto">
                  Votre période d'essai de 7 jours est arrivée à son terme. 
                  Pour continuer à utiliser Boost-StrategIA et accéder à toutes vos études, 
                  passez à un abonnement Pro ou Agence.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/pricing">
                    <Button size="lg" className="shadow-glow-md">
                      <CreditCard className="w-5 h-5 mr-2" />
                      Passer au Plan Pro - 79€/mois
                    </Button>
                  </Link>
                  <Link to="/pricing">
                    <Button size="lg" variant="outline">
                      Voir toutes les formules
                    </Button>
                  </Link>
                </div>
                <p className="text-xs text-muted mt-4">
                  Vos données et études sont conservées. Vous y aurez accès dès votre souscription.
                </p>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Bannière Essai Gratuit 7 jours - PAS pour les promo_no_cb */}
        {subscription?.is_trial && subscription?.status !== 'trial_expired' && 
         subscription?.status !== 'promo_no_cb' && !subscription?.plan?.startsWith('promo_') && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="p-6 border-2 border-amber-500/50 mb-8 bg-gradient-to-r from-amber-500/10 to-orange-500/10" data-testid="trial-banner">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-lg text-amber-400">
                    🎁 Période d'essai gratuit en cours
                  </h3>
                  <p className="text-sm text-muted">
                    Tu profites actuellement de 7 jours d'essai gratuit{subscription?.plan !== 'trial_no_cb' && (
                      <> sur le plan <strong className="text-white">{subscription?.plan === 'agency' ? 'Agence' : 'Professionnel'}</strong></>
                    )}.
                    {subscription?.trial_ends_at && (
                      <span className="block mt-1">
                        Fin de l'essai : <strong className="text-amber-400">
                          {new Date(subscription.trial_ends_at).toLocaleDateString('fr-FR', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </strong>
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-muted mt-2">
                    <strong>Restrictions :</strong> Export PDF non disponible pendant l'essai
                  </p>
                </div>
                {subscription?.plan !== 'trial_no_cb' && (
                  <Button 
                    variant="outline" 
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                    onClick={handleCancelTrial}
                    data-testid="cancel-trial-btn"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Annuler l'essai
                  </Button>
                )}
                {subscription?.plan === 'trial_no_cb' && (
                  <Link to="/pricing">
                    <Button className="shadow-glow-sm">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Passer au Pro
                    </Button>
                  </Link>
                )}
              </div>
            </Card>
          </motion.div>
        )}

        {/* Bannière pour les utilisateurs avec CODE PROMO (promo_no_cb) */}
        {(subscription?.status === 'promo_no_cb' || subscription?.plan?.startsWith('promo_')) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="p-6 border-2 border-blue-500/50 mb-8 bg-gradient-to-r from-blue-500/10 to-purple-500/10" data-testid="promo-banner">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <Gift className="w-6 h-6 text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-lg text-blue-400">
                    🎉 Accès offert activé !
                  </h3>
                  <p className="text-sm text-muted">
                    Tu bénéficies d'un accès complet au plan <strong className="text-white">Professionnel</strong>.
                    {subscription?.trial_ends_at && (
                      <span className="block mt-1">
                        Valide jusqu'au : <strong className="text-blue-400">
                          {new Date(subscription.trial_ends_at).toLocaleDateString('fr-FR', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </strong>
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-green-400 mt-2">
                    ✅ Export PDF inclus • ✅ Accès complet à toutes les fonctionnalités
                  </p>
                </div>
                <Link to="/pricing">
                  <Button variant="outline" className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10">
                    <CreditCard className="w-4 h-4 mr-2" />
                    Passer au Pro
                  </Button>
                </Link>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Video de bienvenue avec recommandations */}
        <Card className="p-6 border-border mb-8 bg-gradient-to-r from-primary/10 to-accent/10" data-testid="welcome-video-section">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
              <Video className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg">
                {subscription?.plan === 'free' ? '🎬 Découvre Boost-StrategIA' : '🎬 Bienvenue dans Boost-StrategIA Pro !'}
              </h3>
            </div>
          </div>
          
          {/* Layout vidéo + recommandations */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Video embed - plus petit */}
            <div className="aspect-video w-full rounded-lg overflow-hidden">
              {subscription?.plan === 'free' ? (
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/_dwynTNgAMw"
                  title="Découvre Boost-StrategIA"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/ynGWTB0HAOg"
                  title="Bienvenue dans Boost-StrategIA Pro"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
            
            {/* Recommandations */}
            <div className="space-y-4">
              <h4 className="font-semibold text-primary">📋 Quelques recommandations :</h4>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-pink-400 mt-0.5">•</span>
                  <div>
                    <strong className="text-white">Si vous avez plusieurs offres :</strong>
                    <p className="text-muted">Ne traitez qu'UNE SEULE offre à la fois.<br/>1 offre / conversation pour travailler précisément sur 1 avatar ou sur 1 offre</p>
                  </div>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400 mt-0.5">•</span>
                  <p className="text-muted">Vous pouvez <strong className="text-white">interrompre l'étude</strong> sur Boost-StrategIA et la reprendre plus tard.</p>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400 mt-0.5">•</span>
                  <p className="text-muted">Pensez à <strong className="text-white">générer le PDF</strong> qui récapitule vos résultats APRÈS chaque étape.</p>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-pink-400 mt-0.5">•</span>
                  <p className="text-muted">Et pour toutes questions, n'hésitez pas à <a href="https://www.linkedin.com/in/valeriemadej/" target="_blank" rel="noreferrer" className="text-primary hover:underline">me contacter sur LinkedIn</a></p>
                </li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Bonus Coaching pour abonnés */}
        {subscription?.plan !== 'free' && subscription?.billing_cycle === 'monthly' && (
          <Card className="p-6 border-accent/50 mb-8 bg-accent/5" data-testid="coaching-section">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center">
                <Calendar className="w-6 h-6 text-accent" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-semibold text-lg">🎯 Tes 2 Coachings Live Groupés</h3>
                <p className="text-sm text-muted">
                  En tant qu'abonné mensuel, tu bénéficies de 2 sessions de coaching live par mois pour répondre à tes questions !
                </p>
              </div>
              <Button variant="outline" className="border-accent text-accent hover:bg-accent/10" disabled>
                Prochaine session bientôt
              </Button>
            </div>
          </Card>
        )}

        {/* Section Achat d'études supplémentaires pour abonnés payants (pas pour trial_no_cb) */}
        {subscription?.plan !== 'free' && subscription?.plan !== 'trial_no_cb' && subscription?.status !== 'trial_no_cb' && (
          <Card className="p-6 border-highlight/50 mb-8 bg-highlight/5" data-testid="extra-study-section">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-highlight/20 rounded-full flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-highlight" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading font-semibold text-lg">🚀 Besoin d'une étude supplémentaire ?</h3>
                <p className="text-sm text-muted">
                  Un nouvel avatar client/persona ou une nouvelle offre à tester ? Achète une étude supplémentaire pour approfondir ta stratégie !
                </p>
              </div>
              <Button 
                className="bg-highlight text-black hover:bg-highlight/90 shadow-glow-md"
                onClick={handleBuyExtraStudy}
                data-testid="buy-extra-study-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Acquérir une étude 29€
              </Button>
            </div>
          </Card>
        )}

        {/* Section Upgrade pour utilisateurs GRATUITS qui ont terminé leur test */}
        {subscription?.plan === 'free' && studies.some(s => s.current_step >= FREE_STEPS_LIMIT) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <Card className="p-8 border-2 border-primary/50 bg-gradient-to-br from-primary/10 via-accent/5 to-highlight/10" data-testid="upgrade-section">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Rocket className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold mb-3">
                  Tu as vu un aperçu de ta stratégie... <span className="text-primary">Imagine la suite !</span>
                </h2>
                <p className="text-muted text-lg max-w-2xl mx-auto">
                  En seulement 3 étapes, tu as déjà des insights précieux. Imagine le <strong className="text-highlight">boost incroyable</strong> que tu pourrais donner à ton activité avec l'analyse complète en <strong>50 étapes</strong> !
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* Plan Pro */}
                <Card className="p-6 border-primary/30 bg-card hover:border-primary transition-all">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-5 h-5 text-primary" />
                    <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">Recommandé</span>
                  </div>
                  <h3 className="text-xl font-heading font-bold mb-1">Professionnel</h3>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-3xl font-bold">79€</span>
                    <span className="text-muted">/mois</span>
                  </div>
                  <p className="text-sm text-primary mb-4">Ou 790€ /an - 2 mois offerts</p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary" />
                      2 études complètes par mois
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary" />
                      Accès aux 50 étapes
                    </li>
                    <li className="flex items-center gap-2 text-sm text-accent font-semibold">
                      <Check className="w-4 h-4 text-accent" />
                      2 coachings live groupés/mois
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary" />
                      Export PDF + Bonus Légende
                    </li>
                  </ul>
                  <Button 
                    className="w-full shadow-glow-md" 
                    onClick={() => navigate('/pricing')}
                    data-testid="upgrade-pro-btn"
                  >
                    Débloquer maintenant
                  </Button>
                </Card>

                {/* Plan Agence */}
                <Card className="p-6 border-border bg-card hover:border-accent transition-all">
                  <div className="h-6 mb-2"></div>
                  <h3 className="text-xl font-heading font-bold mb-1">Agence</h3>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="text-3xl font-bold">199€</span>
                    <span className="text-muted">/mois</span>
                  </div>
                  <p className="text-sm text-primary mb-4">Ou 1990€ /an - 2 mois offerts</p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary" />
                      5 études complètes par mois
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary" />
                      Tous les avantages Pro
                    </li>
                    <li className="flex items-center gap-2 text-sm text-accent font-semibold">
                      <Check className="w-4 h-4 text-accent" />
                      2 coachings live groupés/mois
                    </li>
                    <li className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary" />
                      Multi-utilisateurs + Formation
                    </li>
                  </ul>
                  <Button 
                    className="w-full shadow-glow-md" 
                    onClick={() => navigate('/pricing')}
                    data-testid="upgrade-agency-btn"
                  >
                    Débloquer maintenant
                  </Button>
                </Card>
              </div>

              <p className="text-center text-sm text-muted">
                💡 <strong>Économise 2 mois</strong> avec l'abonnement annuel ! 
                <Link to="/pricing" className="text-primary hover:underline ml-1">Voir toutes les offres</Link>
              </p>
            </Card>
          </motion.div>
        )}

        {/* Stats - masqué pour trial_no_cb mais affiché pour promo_no_cb */}
        {subscription?.plan !== 'trial_no_cb' && subscription?.status !== 'trial_no_cb' && subscription?.status !== 'trial_expired' && (
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className={`p-6 border-border ${subscription?.is_trial && subscription?.status !== 'promo_no_cb' && !subscription?.plan?.startsWith('promo_') ? 'border-amber-500/50 bg-amber-500/5' : subscription?.status === 'promo_no_cb' || subscription?.plan?.startsWith('promo_') ? 'border-blue-500/50 bg-blue-500/5' : ''}`} data-testid="stats-plan">
            <div className="text-sm text-muted mb-1">Plan actuel</div>
            <div className="text-2xl font-heading font-semibold capitalize" data-testid="current-plan">
              {subscription?.status === 'promo_no_cb' || subscription?.plan?.startsWith('promo_') ? (
                <span className="text-blue-400">
                  Accès Pro offert
                </span>
              ) : subscription?.is_trial ? (
                <span className="text-amber-400">
                  Essai {subscription?.plan === 'agency' ? 'Agence' : 'Pro'}
                </span>
              ) : (
                subscription?.plan === 'agency' ? 'Agence' : 
                subscription?.plan === 'pro' ? 'Pro' : 
                subscription?.plan || 'Gratuit'
              )}
            </div>
            {(subscription?.status === 'promo_no_cb' || subscription?.plan?.startsWith('promo_')) && subscription?.trial_ends_at && (
              <div className="text-xs text-blue-400/70 mt-1">
                Fin : {new Date(subscription.trial_ends_at).toLocaleDateString('fr-FR')}
              </div>
            )}
            {subscription?.is_trial && subscription?.status !== 'promo_no_cb' && !subscription?.plan?.startsWith('promo_') && subscription?.trial_ends_at && (
              <div className="text-xs text-amber-400/70 mt-1">
                Fin : {new Date(subscription.trial_ends_at).toLocaleDateString('fr-FR')}
              </div>
            )}
          </Card>

          <Card className={`p-6 border-border ${subscription?.is_trial && subscription?.status !== 'promo_no_cb' && !subscription?.plan?.startsWith('promo_') ? 'border-amber-500/50 bg-amber-500/5' : subscription?.status === 'promo_no_cb' || subscription?.plan?.startsWith('promo_') ? 'border-blue-500/50 bg-blue-500/5' : ''}`} data-testid="stats-studies">
            <div className="text-sm text-muted mb-1">Études restantes</div>
            <div className="text-2xl font-heading font-semibold" data-testid="studies-remaining">
              {subscription?.studies_remaining ?? (subscription?.is_trial ? 1 : 0)}
            </div>
            {subscription?.is_trial && subscription?.status !== 'promo_no_cb' && !subscription?.plan?.startsWith('promo_') && (
              <div className="text-xs text-amber-400/70 mt-1">
                Limité à 1 en essai
              </div>
            )}
          </Card>

          <Card className="p-6 border-border" data-testid="stats-total">
            <div className="text-sm text-muted mb-1">Études totales</div>
            <div className="text-2xl font-heading font-semibold" data-testid="total-studies">
              {studies.length}
            </div>
          </Card>
        </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-heading font-bold" data-testid="dashboard-title">Mes Études</h1>
          <div className="flex gap-3">
            {/* Bouton Légende uniquement pour les abonnés payants */}
            {subscription?.plan !== 'free' && (
              <Button 
                variant="outline" 
                onClick={() => navigate('/legend')}
                className="border-highlight text-highlight hover:bg-highlight/10"
                data-testid="bonus-legend-button"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Bonus : Ma Légende
              </Button>
            )}
            <Button onClick={handleNewStudy} className="shadow-glow-md" data-testid="new-study-button">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle étude
            </Button>
          </div>
        </div>

        {/* Studies List */}
        {studies.length === 0 ? (
          <div className="text-center py-20" data-testid="no-studies">
            <TrendingUp className="w-16 h-16 text-muted mx-auto mb-4" />
            <h3 className="text-xl font-heading font-semibold mb-2">Aucune étude pour le moment</h3>
            <p className="text-muted mb-6">Commencez votre première analyse marketing</p>
            <Button onClick={handleNewStudy} className="shadow-glow-md" data-testid="first-study-button">
              Créer ma première étude
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {studies.map((study, i) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.05 }}
              >
                <Link to={`/study/${study.id}`} data-testid={`study-card-${study.id}`}>
                  <Card className="p-6 border-border hover:border-primary/50 transition-all cursor-pointer">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="font-heading font-semibold text-lg" data-testid={`study-title-${study.id}`}>
                        {study.title}
                      </h3>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          study.status === 'completed'
                            ? 'bg-primary/20 text-primary'
                            : 'bg-accent/20 text-accent'
                        }`}
                        data-testid={`study-status-${study.id}`}
                      >
                        {study.status === 'completed' ? 'Terminée' : 'En cours'}
                      </span>
                    </div>
                    <div className="text-sm text-muted">
                      <div data-testid={`study-step-${study.id}`}>Étape {study.current_step} / {TOTAL_STEPS}</div>
                      <div data-testid={`study-date-${study.id}`}>
                        Créée le {new Date(study.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;